import { FetchKey } from '@app/src/api/fetchHooks'
import { NO_PERIOD_PERIOD_NAME } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import { useGroupBySelector } from '@app/src/context/GroupBySelectorContext'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { Grid } from '@mui/material'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import type { FacetItem } from '..'
import { responseItemPeriodFacetSettings } from './Common/ResponseItemFacetSettings'
import Filter from './Filter'
import FilterFacetSelect from './FilterFacetSelect'
import type { FilterInterface } from './Filters'
import { useFiltersContext } from './Filters'
import { AnswersGroupingType, RESPONSE_REQUEST_PERIOD_NAME } from './StatisticsConstants'
import { Operators } from './useFilters'

interface PeriodFilterProps {
  preSelectedValues?: Array<FacetItem['value']>
  allowedFilters: string[]
  anyQuestionFilterSet?: boolean
}

const PERIOD_FILTER_NAME = 'response.request.periodName'

export const DatahubPeriodFilter: React.FC<PeriodFilterProps> = ({
  preSelectedValues,
  allowedFilters,
  anyQuestionFilterSet,
}) => {
  const { formatMessage } = useIntl()
  const { setQueryFilter } = useFiltersContext()
  const { groupBy } = useGroupBySelector()
  const userFilters = useGetApiQueryFilters(allowedFilters)

  const periodFilterValue = userFilters.find(group => group.name === PERIOD_FILTER_NAME)?.filters[0]?.value

  const periodFilterWasCleared = !periodFilterValue

  const selectedPeriodsWithoutNoPeriod = preSelectedValues?.filter(value => value !== NO_PERIOD_PERIOD_NAME)

  const getSelectProps = (
    onChange: (value: FilterInterface['value'] | null, replace?: boolean) => void,
    value: string | string[],
  ) => {
    if (groupBy === AnswersGroupingType.PeriodName) {
      return {
        key: 'period-name-select',
        multiple: true as const,
        onChange: (value: string[]) => onChange(value),
        value: [value].flat(),
      }
    } else {
      return {
        key: 'spend-classification-select',
        multiple: false as const,
        onChange: (value: string | null) => onChange(value),
        value: value as string,
      }
    }
  }

  useEffect(() => {
    if (selectedPeriodsWithoutNoPeriod?.length && anyQuestionFilterSet) {
      if (periodFilterWasCleared || groupBy === AnswersGroupingType.PeriodName) {
        setQueryFilter(RESPONSE_REQUEST_PERIOD_NAME, selectedPeriodsWithoutNoPeriod as string[], Operators.In)
      } else {
        setQueryFilter(RESPONSE_REQUEST_PERIOD_NAME, selectedPeriodsWithoutNoPeriod[0] as string, Operators.EqualTo)
      }
    }
  }, [groupBy, periodFilterWasCleared, JSON.stringify(selectedPeriodsWithoutNoPeriod), anyQuestionFilterSet])

  return (
    <Grid item xs={2}>
      <Filter
        name={PERIOD_FILTER_NAME}
        operator={groupBy === AnswersGroupingType.PeriodName ? Operators.In : Operators.EqualTo}
      >
        {({ value, onChange, filterName }) => (
          <FilterFacetSelect
            {...getSelectProps(onChange, value)}
            size="small"
            facetsParam={responseItemPeriodFacetSettings([FetchKey.ResponseItemsFacets, filterName])}
            filterName={filterName}
            fieldLabel={formatMessage({ id: 'schemas.request.period' })}
          />
        )}
      </Filter>
    </Grid>
  )
}
